/* istanbul ignore file */
import React from "react";
import PropTypes from "prop-types";
import { SummaryDataItem, InventoryDataItem } from "../SummaryCard";

const numericish = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

const serviceExpandedDistrictShape = PropTypes.shape({
  balloonSalesAmount: numericish,
  balloonTotalSalesUnits: numericish,
});

const serviceExpandedRegionShape = PropTypes.shape({
  balloonSalesAmount: numericish,
  balloonTotalSalesUnits: numericish,
  districtList: PropTypes.arrayOf(serviceExpandedDistrictShape),
});

const serviceExpandedChainShape = PropTypes.shape({
  balloonSalesAmount: numericish,
  balloonTotalSalesUnits: numericish,
});

const omniMetricsShape = {
  bopisFulfilledOrdersCount: numericish,
  bopisUnitsPickRate: numericish,
};

const omniDistrictShape = PropTypes.shape(omniMetricsShape);

const omniRegionShape = PropTypes.shape({
  ...omniMetricsShape,
  districtList: PropTypes.arrayOf(omniDistrictShape),
});

const omniChainShape = PropTypes.shape(omniMetricsShape);

const inventoryDistrictShape = PropTypes.shape({
  replaceUnitQuantity: numericish,
  damagesUnitQuantity: numericish,
  mosUnitQuantity: numericish,
  knownTheftUnitQuantity: numericish,
  shipmentUnitQuantity: numericish,
  candyUnitQuantity: numericish,
});

const inventoryRegionShape = PropTypes.shape({
  replaceUnitQuantity: numericish,
  damagesUnitQuantity: numericish,
  mosUnitQuantity: numericish,
  knownTheftUnitQuantity: numericish,
  shipmentUnitQuantity: numericish,
  candyUnitQuantity: numericish,
  districtList: PropTypes.arrayOf(inventoryDistrictShape),
});

const inventoryChainShape = PropTypes.shape({
  replaceUnitQuantity: numericish,
  damagesUnitQuantity: numericish,
  mosUnitQuantity: numericish,
  knownTheftUnitQuantity: numericish,
  shipmentUnitQuantity: numericish,
  candyUnitQuantity: numericish,
});

const CHAIN_SUMMARY_TITLE = "Chain Summary";
const REGION_SUMMARY_TITLE = "Region Summary";

const pickInventoryQuantity = (title, chain, regionList, quantityKey) => {
  if (title === CHAIN_SUMMARY_TITLE) {
    return Number.parseInt(chain?.[quantityKey] ?? 0);
  }
  if (title === REGION_SUMMARY_TITLE) {
    return Number.parseInt(regionList[0]?.[quantityKey] ?? 0);
  }
  return Number.parseInt(regionList[0]?.districtList[0]?.[quantityKey] ?? 0);
};

const formatInventoryQuantity = (title, chain, regionList, quantityKey) =>
  Math.round(pickInventoryQuantity(title, chain, regionList, quantityKey)).toLocaleString(
    "en"
  );

const pickExpandedRawValue = (title, chain, regionList, key) => {
  if (title === CHAIN_SUMMARY_TITLE) {
    return chain?.[key] ?? 0;
  }
  if (title === REGION_SUMMARY_TITLE) {
    return regionList[0]?.[key] ?? 0;
  }
  return regionList[0]?.districtList[0]?.[key] ?? 0;
};

const formatServiceDollarAmount = (title, chain, regionList, key) =>
  `$${Math.round(
    Number.parseFloat(pickExpandedRawValue(title, chain, regionList, key))
  ).toLocaleString("en")}`;

const formatServiceUnitAmount = (title, chain, regionList, key) =>
  Math.round(
    Number.parseFloat(pickExpandedRawValue(title, chain, regionList, key))
  ).toLocaleString("en");

const formatOmniCount = (title, chain, regionList, key) =>
  Math.round(pickExpandedRawValue(title, chain, regionList, key)).toLocaleString(
    "en-US"
  );

const formatOmniPercent = (title, chain, regionList, key) =>
  `${Math.round(pickExpandedRawValue(title, chain, regionList, key)).toLocaleString(
    "en"
  )}%`;

const INVENTORY_MODAL_SUMMARY_ITEMS = [
  { itemTitle: "Replace", quantityKey: "replaceUnitQuantity" },
  { itemTitle: "Damages", quantityKey: "damagesUnitQuantity" },
  { itemTitle: "MOS", quantityKey: "mosUnitQuantity" },
  { itemTitle: "Known Theft", quantityKey: "knownTheftUnitQuantity" },
  { itemTitle: "Shipment", quantityKey: "shipmentUnitQuantity" },
  { itemTitle: "Candy", quantityKey: "candyUnitQuantity" },
];

export function SalesSummaryModalServiceContent({
  expandedServiceTopLevelLoading,
  expandedServiceTopLevelError,
  serviceExpandedTitle,
  serviceExpandedChain,
  serviceExpandedRegionList,
}) {
  if (expandedServiceTopLevelLoading) {
    return null;
  }
  if (expandedServiceTopLevelError) {
    return (
      <>
        <SummaryDataItem itemAmount={0} itemTitle={"Ballon\nSales $"} />
        <SummaryDataItem itemAmount={0} itemTitle={"Balloon\nSales U"} />
      </>
    );
  }
  return (
    <>
      <SummaryDataItem
        itemAmount={formatServiceDollarAmount(
          serviceExpandedTitle,
          serviceExpandedChain,
          serviceExpandedRegionList,
          "balloonSalesAmount"
        )}
        itemTitle={"Ballon\nSales $"}
      />
      <SummaryDataItem
        itemAmount={formatServiceUnitAmount(
          serviceExpandedTitle,
          serviceExpandedChain,
          serviceExpandedRegionList,
          "balloonTotalSalesUnits"
        )}
        itemTitle={"Balloon\nSales U"}
      />
    </>
  );
}

SalesSummaryModalServiceContent.propTypes = {
  expandedServiceTopLevelLoading: PropTypes.bool,
  expandedServiceTopLevelError: PropTypes.bool,
  serviceExpandedTitle: PropTypes.string,
  serviceExpandedChain: serviceExpandedChainShape,
  serviceExpandedRegionList: PropTypes.arrayOf(serviceExpandedRegionShape),
};

const OMNI_MODAL_SUMMARY_ITEMS = [
  {
    itemTitle: "BOPIS Orders \nFulfilled",
    key: "bopisFulfilledOrdersCount",
    format: "count",
  },
  {
    itemTitle: "BOPIS Pick \nRate",
    key: "bopisUnitsPickRate",
    format: "percent",
  },
];

const formatOmniModalAmount = (
  format,
  title,
  chain,
  regionList,
  key
) =>
  format === "count"
    ? formatOmniCount(title, chain, regionList, key)
    : formatOmniPercent(title, chain, regionList, key);

export function SalesSummaryModalOmniContent({
  expandedOmniChannelTopLevelLoading,
  expandedOmniChannelTopLevelError,
  omniChannelExpandedTitle,
  omniChannelExpandedChain,
  omniChannelExpandedRegionList,
}) {
  if (expandedOmniChannelTopLevelLoading) {
    return null;
  }
  if (expandedOmniChannelTopLevelError) {
    return (
      <>
        {OMNI_MODAL_SUMMARY_ITEMS.map(({ itemTitle }) => (
          <SummaryDataItem
            key={itemTitle}
            itemAmount={0}
            itemTitle={itemTitle}
          />
        ))}
      </>
    );
  }
  return (
    <>
      {OMNI_MODAL_SUMMARY_ITEMS.map(({ itemTitle, key, format }) => (
        <SummaryDataItem
          key={itemTitle}
          itemAmount={formatOmniModalAmount(
            format,
            omniChannelExpandedTitle,
            omniChannelExpandedChain,
            omniChannelExpandedRegionList,
            key
          )}
          itemTitle={itemTitle}
        />
      ))}
    </>
  );
}

SalesSummaryModalOmniContent.propTypes = {
  expandedOmniChannelTopLevelLoading: PropTypes.bool,
  expandedOmniChannelTopLevelError: PropTypes.bool,
  omniChannelExpandedTitle: PropTypes.string,
  omniChannelExpandedChain: omniChainShape,
  omniChannelExpandedRegionList: PropTypes.arrayOf(omniRegionShape),
};

export function SalesSummaryModalInventoryContent({
  expandedInventoryloading,
  expandedInventoryerror,
  title,
  chain,
  regionList,
}) {
  if (expandedInventoryloading || expandedInventoryerror) {
    return null;
  }
  return (
    <>
      {INVENTORY_MODAL_SUMMARY_ITEMS.map(({ itemTitle, quantityKey }) => (
        <InventoryDataItem
          key={itemTitle}
          itemAmount={formatInventoryQuantity(
            title,
            chain,
            regionList,
            quantityKey
          )}
          itemTitle={itemTitle}
        />
      ))}
    </>
  );
}

SalesSummaryModalInventoryContent.propTypes = {
  expandedInventoryloading: PropTypes.bool,
  expandedInventoryerror: PropTypes.bool,
  title: PropTypes.string,
  chain: inventoryChainShape,
  regionList: PropTypes.arrayOf(inventoryRegionShape),
};


